/* eslint-disable react/jsx-pascal-case */
import { GridFooter } from "@mui/x-data-grid-pro";
import { useContext, useEffect, useMemo, useState } from "react";
import CustomToolbar from "../../../components/grid/CustomToolbar";
import GeneralContext from "../../../context/GeneralContext/GeneralContext";
import api from "../../../services/api";
import { gym_workout_grid_columns_stage2 } from "./grid_columns/gym_workout_grid_columns";
import { displayMessage } from "../../../helpers/messages";
import SL_Button from "../../../components/buttons/Button";
import { Modal } from "antd";
import {
    ArrowLeftOutlined,
    DeleteOutlined,
    EyeOutlined,
    PlusCircleOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import { SL_Grid } from "../../../components/grid/SL_Grid";
import { CreateGymWorkoutModal } from "./CreateGymWorkoutModal";
import { DetailGymWorkoutModal } from "./DetailGymWorkoutModal";
import { CombinationsStage } from "../../../components/combinations/CombinationsStage";

// Mapeamento de dor no corpo para exibição no card de combinação
const BODY_PAIN_LABELS = {
    none: "Sem dor",
    lower_back: "Lombar",
    shoulder: "Ombro",
    knee: "Joelho",
};

// Constrói a chave + rótulo da combinação (gender + weekly_frequency + body_pain + duration_minutes)
const buildGymCombination = (item) => {
    const gender = item.gender_description || item.gender || "?";
    const freq = item.weekly_frequency ? `${item.weekly_frequency}x/sem` : "?";
    const pain = BODY_PAIN_LABELS[item.body_pain] || item.body_pain || "Sem dor";
    const dur = item.duration_minutes ? `${item.duration_minutes}min` : "?";
    return {
        key: `${item.gender}|${item.weekly_frequency}|${item.body_pain}|${item.duration_minutes}`,
        label: `${gender} • ${freq} • ${pain} • ${dur}`,
    };
};

// Opções dos filtros por dimensão (Etapa 1). Mantidas alinhadas às opções
// usadas no modal de criação para consistência.
const GENDER_FILTERS = [
    { value: "male", label: "Masculino" },
    { value: "female", label: "Feminino" },
];
const FREQUENCY_FILTERS = [
    { value: 1, label: "1x/sem" },
    { value: 2, label: "2x/sem" },
    { value: 3, label: "3x/sem" },
    { value: 4, label: "4x/sem" },
    { value: 5, label: "5x/sem" },
    { value: 6, label: "6x/sem" },
    { value: 7, label: "7x/sem" },
];
const BODY_PAIN_FILTERS = [
    { value: "none", label: "Sem dor" },
    { value: "lower_back", label: "Lombar" },
    { value: "shoulder", label: "Ombro" },
    { value: "knee", label: "Joelho" },
];
const DURATION_FILTERS = [
    { value: 40, label: "40 min" },
    { value: 60, label: "60 min" },
    { value: 90, label: "90 min" },
];

const GYM_FILTER_CONFIGS = [
    { field: "gender", label: displayMessage("GENDER"), options: GENDER_FILTERS },
    { field: "weekly_frequency", label: displayMessage("WEEKLY_FREQUENCY"), options: FREQUENCY_FILTERS },
    { field: "body_pain", label: "Dor no corpo", options: BODY_PAIN_FILTERS },
    { field: "duration_minutes", label: displayMessage("DURATION_MINUTES"), options: DURATION_FILTERS },
];

// Extrai os campos da combinação a partir de um item representativo do grupo,
// para pré-preencher o modal de criação na Etapa 2.
const buildGymPreset = (item) => ({
    gender: item.gender,
    weekly_frequency: item.weekly_frequency,
    body_pain: item.body_pain,
    duration_minutes: item.duration_minutes,
});

export function ListGymWorkouts() {
	const [modal, contextHolder] = Modal.useModal();

	const [workouts, setWorkouts] = useState(null);
	const [currentRow, setCurrentRow] = useState(null);
	const [isModalCreateOpen, setIsModalCreateOpen] = useState(false);
	const [isModalDetailOpen, setIsModalDetailOpen] = useState(false);
	// Etapa 1: selectedCombination === null
	// Etapa 2: selectedCombination = grupo selecionado { key, label, items, seqs, missing }
	const [selectedCombination, setSelectedCombination] = useState(null);
	const [presetCombination, setPresetCombination] = useState(null);

	const { setShowLoading, mobile_mode } = useContext(GeneralContext);

	useEffect(() => {
		loadWorkouts();
	}, []);

	// Sempre que trocamos de combinação (ou voltamos), limpamos a seleção de
	// linha para não acionar ações (ver/remover) sobre um treino de outro grupo.
	useEffect(() => {
		setCurrentRow(null);
	}, [selectedCombination]);

	function handleRowChange(e) {
		setCurrentRow(e?.row);
	}

	// Criação "livre" (Etapa 1) — sem preset, modal completo.
	function onClickCreate() {
		setPresetCombination(null);
		setIsModalCreateOpen(true);
	}

	// Criação "dentro da combinação" (Etapa 2) — preset travado.
	function onClickCreateInCombination() {
		const first = stage2Rows[0];
		setPresetCombination(first ? buildGymPreset(first) : null);
		setIsModalCreateOpen(true);
	}

	async function onCloseCreateModal(loadData = false) {
		if (loadData) {
			await loadWorkouts();
		}
		setIsModalCreateOpen(false);
		setPresetCombination(null);
	}

	function onClickDetail() {
		setIsModalDetailOpen(true);
	}

	async function onCloseDetailModal(loadData = false) {
		if (loadData) {
			await loadWorkouts();
		}
		setIsModalDetailOpen(false);
	}

	async function loadWorkouts() {
		setShowLoading(true);
		try {
			const response = await api.gymWorkouts.list();
			setWorkouts(response.data);
		} catch (error) {
			console.error("Erro ao carregar treinos:", error);
		} finally {
			setShowLoading(false);
		}
	}

	function onClickRemove() {
		modal.confirm({
			centered: true,
			content: (
				<div className="text-base w-full text-fit-red-500 text-center sm:text-start">
					{displayMessage("CONFIRM_REMOVE_WORKOUT")}
				</div>
			),
			cancelText: displayMessage("CANCEL"),
			okText: displayMessage("CONFIRM"),
			okButtonProps: {
				danger: true,
			},
			onOk: removeWorkout,
			icon: mobile_mode ? null : <WarningOutlined style={{ color: "red" }} />,
			cancelButtonProps: {
				style: { color: "gray !important" },
			},
		});
	}

	async function removeWorkout() {
		try {
			setShowLoading(true);
			await api.gymWorkouts.delete(currentRow.id);
			setCurrentRow(null);
			await loadWorkouts();
		} finally {
			setShowLoading(false);
		}
	}

	// Itens da combinação selecionada, derivados do estado `workouts`
	// (sempre fresco após create/remove). Armazenamos apenas a chave + rótulo
	// da combinação em `selectedCombination`, e aqui filtramos/ordenamos em
	// memória — por isso a Etapa 2 atualiza sozinha ao recarregar a lista.
	const stage2Rows = useMemo(() => {
		if (!selectedCombination) return [];
		return (workouts || [])
			.filter((w) => buildGymCombination(w).key === selectedCombination.key)
			.sort((a, b) => {
				const sa = a.sequence ?? 1;
				const sb = b.sequence ?? 1;
				if (sa !== sb) return sa - sb;
				return (a.order_index ?? 0) - (b.order_index ?? 0);
			});
	}, [workouts, selectedCombination]);

	return (
		<>
			{contextHolder}
			{isModalCreateOpen && (
				<CreateGymWorkoutModal
					onCloseModal={onCloseCreateModal}
					presetCombination={presetCombination}
				/>
			)}
			{isModalDetailOpen && currentRow && (
				<DetailGymWorkoutModal
					onCloseModal={onCloseDetailModal}
					workoutId={currentRow.id}
				/>
			)}

			<div className="flex flex-col flex-1 h-full">
				<div className="flex flex-1 flex-col">
					<div className="flex flex-col flex-1 h-full">
						<div
							className={`flex w-full bg-blue-600 p-2 rounded-tl-[4px] rounded-tr-[4px] flex-wrap ${
								mobile_mode ? "flex-col items-center gap-4" : "gap-2"
							}`}
						>
							<div className="self-center text-white font-semibold text-base">
								{displayMessage("GYM_WORKOUT_LIST")}
							</div>

							<div className={`flex-1 flex flex-wrap gap-2 justify-end`}>
								<div
									className={`flex-1 flex ${
										mobile_mode ? "flex-col" : "flex-wrap"
									} gap-2 justify-end`}
								>
									{selectedCombination && (
										<SL_Button
											type="default"
											className={`flex items-center flex-1 w-full ss:max-w-[220px] ss:min-w-[160px]`}
											onClick={() => setSelectedCombination(null)}
											icon={
												<ArrowLeftOutlined className="text-base flex items-center" />
											}
										>
											<span className="flex-1">
												{displayMessage("COMBINATIONS_BACK")}
											</span>
										</SL_Button>
									)}

									{selectedCombination && (
										<SL_Button
											type="default"
											disabled={!currentRow}
											className={`flex items-center flex-1 w-full ss:max-w-[200px] ss:min-w-[200px]`}
											onClick={onClickDetail}
											icon={
												<EyeOutlined className="text-base flex items-center" />
											}
										>
											<span className="flex-1">
												{displayMessage("VIEW_WORKOUT")}
											</span>
										</SL_Button>
									)}

									<SL_Button
										type="default"
										className={`flex items-center flex-1 w-full ss:max-w-[260px] ss:min-w-[200px]`}
										onClick={
											selectedCombination
												? onClickCreateInCombination
												: onClickCreate
										}
										icon={
											<PlusCircleOutlined className="text-base flex items-center" />
										}
									>
										<span className="flex-1">
											{selectedCombination
												? displayMessage("COMBINATIONS_ADD_IN_WORKOUT")
												: displayMessage("ADD_WORKOUT")}
										</span>
									</SL_Button>

									{selectedCombination && (
										<SL_Button
											disabled={!currentRow}
											className={`flex items-center flex-1 w-full ss:max-w-[200px] ss:min-w-[200px]`}
											danger={true}
											onClick={onClickRemove}
											icon={
												<DeleteOutlined className="text-base flex items-center" />
											}
										>
											<span className="flex-1">
												{displayMessage("REMOVE_WORKOUT")}
											</span>
										</SL_Button>
									)}
								</div>
							</div>
					</div>

					{/* Etapa 1 — Agrupamentos */}
					{!selectedCombination && (
						<CombinationsStage
							items={workouts ?? []}
							buildCombination={buildGymCombination}
							filterConfigs={GYM_FILTER_CONFIGS}
							itemNamePlural={displayMessage("COMBINATIONS_ITEMS_WORKOUT")}
							onSelect={(g) =>
								setSelectedCombination({ key: g.key, label: g.label })
							}
						/>
					)}

					{/* Etapa 2 — Itens da combinação, ordenados por sequência */}
					{selectedCombination && (
						<>
							<div className="px-2 py-1 bg-blue-50 border-b border-blue-100">
								<div className="text-blue-700 font-semibold text-sm">
									{displayMessage("COMBINATIONS_ITEMS_IN")}
								</div>
								<div className="text-gray-600 text-sm">
									{selectedCombination.label}
								</div>
							</div>

							<div className={`w-full flex-1 min-h-[400px] smb:min-h-0`}>
								<SL_Grid
									rows={stage2Rows}
									columns={gym_workout_grid_columns_stage2}
									components={{
										Toolbar: () => <CustomToolbar />,
										Footer: () => <GridFooter />,
									}}
									onRowClick={handleRowChange}
								/>
							</div>
						</>
					)}
					</div>
				</div>
			</div>
		</>
	);
}
